import { TourProvider } from "@/libs/tours/data/provider";
import { TourOverviewPage } from "@/pages/tour";
import { TourDetailsPage } from "@/pages/tour/details";
import { ErrorBoundary } from "@sentry/react";
import {
  Link,
  Route,
  BrowserRouter as RouterProvider,
  Routes,
  useLocation,
} from "react-router-dom";

import { UserProvider, useUser } from "@/libs/auth/data/user";
import { LoginPage } from "@/pages/LoginPage";
import { TourSummaryPage } from "@/pages/TourSummaryPage";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { Toaster } from "./components/ui/toaster";

import { TourDetailProvider } from "./libs/tours-detail/data/provider";
import { AlternativeDeliveryPage } from "./pages/AlternativeDeliveryPage";
import { DeliveryFailedPage } from "./pages/DeliveryFailedPage";
import { DeliveryStopPage } from "./pages/DeliveryStopPage";
import { DropOffStopPage } from "./pages/DropOffStopPage";
import { PickUpPage } from "./pages/pickup";

import { LoadingSpinner } from "./components/LoadingSpinner";
import { DriverProvider, useDriver } from "./libs/auth/data/driver";
import { DriverErrorView } from "./libs/auth/views/DriverErrorView";
import { SettingsProvider } from "./libs/settings/data/provider";
import { AlternativeStorageDeliveryPage } from "./pages/AlternativeStoragePage";
import { DeliveryDetailPage } from "./pages/DeliveryDetailPage";
import { DropOffDetailPage } from "./pages/DropOffDetailPage";
import GatherPickupDetailPage from "./pages/pickup/GatherPickupDetailPage";
import { isDevelopment } from "@/utils/config";

import "@capacitor-community/safe-area";
import { MessageCircleIcon } from "lucide-react";
import { ChatsPage } from "./pages/chat";
import { ChatDetailPage } from "./pages/chat/detail";
import { ChatProvider } from "./libs/chat/data/provider";

export type Path = `/${string}`;
type RoutePaths = Record<string, Path | ((...args: any[]) => Path)>;

export const routePaths = {
  login: "/",
  home: "/home",
  tour: "/tour/:id",
  chat: "/chat",
  createChatPath: function (id: string) {
    return `/chat/${id}`;
  },
  createTourPath: function (id: string) {
    return `/tour/${id}`;
  },
  /**
   * @TODO In the future, we should make the delivery page generic, in order to
   * avoid the need to create a new page for each type of delivery.
   * Gather tasks, for example, could be handled in the same page as delivery tasks.
   */
  delivery: "/tour/:id/order-delivery/:stopId",
  deliverDetail: "/tour/:id/details/:stopId/:taskId",
  gatherPickupDetail: "/tour/:id/gatherTask-pickup/:stopId/:taskId",
  createGatherPickupDetailPath: function (
    id: string,
    stopId: string,
    taskId: string,
  ) {
    return `/tour/${id}/gatherTask-pickup/${stopId}/${taskId}`;
  },
  dropOff: "/tour/:id/gatherTask-dropOff/:stopId",
  pickup: "/tour/:id/:taskType/:stopId",
  createTourStopPath: function (tourId: string, stopId: string, type: string) {
    return `/tour/${tourId}/${type}/${stopId}`;
  },
  summary: "/tour/:id/summary",
  createTourSummaryPath: function (id: string) {
    return `/tour/${id}/summary`;
  },
  notDelivered: "/tour/:id/order-delivery/:stopId/not-delivered/:taskId",
  createDeliveryFailedPath: function (
    id: string,
    stopId: string,
    taskId: string,
  ) {
    return `/tour/${id}/order-delivery/${stopId}/not-delivered/${taskId}`;
  },
  alternativeDelivery: "/tour/:id/order-delivery/:stopId/alternative/:taskId",
  createAlternativeDeliveryPath: function (
    id: string,
    stopId: string,
    taskId: string,
  ) {
    return `/tour/${id}/order-delivery/${stopId}/alternative/${taskId}`;
  },
  alternativeStorageDelivery:
    "/tour/:id/order-delivery/:stopId/alternative-storage/:taskId",
  createAlternativeStorageDeliveryPath: function (
    id: string,
    stopId: string,
    taskId: string,
  ) {
    return `/tour/${id}/order-delivery/${stopId}/alternative-storage/${taskId}`;
  },
} satisfies RoutePaths;

function App() {
  return (
    <ErrorBoundary>
      <RouterProvider>
        <UserProvider>
          <DriverProvider>
            <SettingsProvider>
              <Toaster />

              <TourProvider>
                <ChatProvider>
                  {isDevelopment && <OfflineIndicator />}
                  <ChatIcon />

                  <Pages />
                </ChatProvider>
              </TourProvider>
            </SettingsProvider>
          </DriverProvider>
        </UserProvider>
      </RouterProvider>
    </ErrorBoundary>
  );
}

function ChatIcon() {
  const location = useLocation();

  if (
    location.pathname.startsWith(routePaths.chat) ||
    location.pathname === routePaths.login
  ) {
    return null;
  }

  return (
    <Link
      to={routePaths.chat}
      className="fixed top-0 right-0 z-50 m-4 p-3 bg-primary rounded-full shadow-lg hover:shadow-xl transition-shadow border"
    >
      <MessageCircleIcon className="text-white" />
    </Link>
  );
}

function Pages() {
  const { isInitialized } = useUser();
  const { isDriverLoaded, hasDriverError } = useDriver();

  const showContent =
    (isInitialized && isDriverLoaded) || location.pathname === routePaths.login;

  if (hasDriverError) {
    return <DriverErrorView />;
  }

  return showContent ? (
    <Routes>
      <Route path={routePaths.home} element={<TourOverviewPage />} />

      <Route
        path={routePaths.deliverDetail}
        element={
          <TourDetailProvider>
            <DeliveryDetailPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={"/tour/:id/gatherTask-dropOff/:stopId/:taskId"}
        element={
          <TourDetailProvider>
            <DropOffDetailPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.gatherPickupDetail}
        element={
          <TourDetailProvider>
            <GatherPickupDetailPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.tour}
        element={
          <TourDetailProvider>
            <TourDetailsPage />
          </TourDetailProvider>
        }
      />

      <Route path={routePaths.chat} element={<ChatsPage />} />
      <Route
        path={routePaths.createChatPath(":id")}
        element={<ChatDetailPage />}
      />

      <Route
        path={routePaths.delivery}
        element={
          <TourDetailProvider>
            <DeliveryStopPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.pickup}
        element={
          <TourDetailProvider>
            <PickUpPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.notDelivered}
        element={
          <TourDetailProvider>
            <DeliveryFailedPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.alternativeDelivery}
        element={
          <TourDetailProvider>
            <AlternativeDeliveryPage />
          </TourDetailProvider>
        }
      />

      <Route
        path={routePaths.alternativeStorageDelivery}
        element={
          <TourDetailProvider>
            <AlternativeStorageDeliveryPage />
          </TourDetailProvider>
        }
      />

      <Route path={routePaths.summary} element={<TourSummaryPage />} />

      <Route
        path={routePaths.dropOff}
        element={
          <TourDetailProvider>
            <DropOffStopPage />
          </TourDetailProvider>
        }
      />

      <Route path={routePaths.login} element={<LoginPage />} />
    </Routes>
  ) : (
    <div className="h-dvh w-full grid place-items-center">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner />
        <span>Account lädt ...</span>
      </div>
    </div>
  );
}

export default App;
